'use client';

import { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import {
  CheckCircle, XCircle, Loader2, Plus, Trash2, FileText, FileX,
  Pencil, ListVideo, ChevronLeft, ChevronRight, Tag, Settings2, Check, X,
} from 'lucide-react';

interface Channel {
  id: string;
  name: string;
  handle: string | null;
  url: string;
}

interface Video {
  id: string;
  channel_id: string;
  title: string;
  status: string;
  chunk_count: number;
  indexed_at: string;
  used_transcript: number;
  transcript_text: string | null;
}

interface IngestResult {
  url: string;
  videoId?: string;
  title?: string;
  chunkCount?: number;
  usedTranscript?: boolean;
  error?: string;
}

const BATCH_SIZE = 3;

function isBulkUrl(url: string): boolean {
  return (/[?&]list=/.test(url) && !/[?&]v=/.test(url)) || /youtube\.com\/@/.test(url);
}

export default function AdminPage() {
  // Channel state
  const [channels, setChannels] = useState<Channel[]>([]);
  const [editingChannelId, setEditingChannelId] = useState<string | null>(null);
  const [editingChannelName, setEditingChannelName] = useState('');
  const [savingChannelId, setSavingChannelId] = useState<string | null>(null);
  const [showChannelManager, setShowChannelManager] = useState(false);

  // Ingest state
  const [urls, setUrls] = useState('');
  const [ingesting, setIngesting] = useState(false);
  const [results, setResults] = useState<IngestResult[] | null>(null);
  const [progress, setProgress] = useState<{ current: number; total: number } | null>(null);

  // Video list state
  const [videos, setVideos] = useState<Video[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filterChannel, setFilterChannel] = useState('');
  const [filterSearch, setFilterSearch] = useState('');
  const [loadingVideos, setLoadingVideos] = useState(false);

  // Multi-select state
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [assigningChannel, setAssigningChannel] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const channelName = useCallback((id: string) => channels.find(c => c.id === id)?.name ?? id, [channels]);

  const fetchChannels = useCallback(async () => {
    const res = await fetch('/api/channels');
    const data = await res.json() as { channels: Channel[] };
    setChannels(data.channels);
  }, []);

  const fetchVideos = useCallback(async (p = page) => {
    setLoadingVideos(true);
    const params = new URLSearchParams({ page: String(p) });
    if (filterChannel) params.set('channelId', filterChannel);
    if (filterSearch)  params.set('search', filterSearch);
    const res = await fetch(`/api/videos?${params}`);
    const data = await res.json() as { videos: Video[]; total: number; page: number; totalPages: number };
    setVideos(data.videos);
    setTotal(data.total);
    setPage(data.page);
    setTotalPages(data.totalPages);
    setSelected(new Set());
    setLoadingVideos(false);
  }, [page, filterChannel, filterSearch]);

  useEffect(() => { fetchChannels(); }, [fetchChannels]);
  useEffect(() => { fetchVideos(1); }, [filterChannel, filterSearch]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { fetchVideos(page); }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Channel rename ---
  function startEditChannel(ch: Channel) {
    setEditingChannelId(ch.id);
    setEditingChannelName(ch.name);
  }

  async function saveChannelName(id: string) {
    if (!editingChannelName.trim()) return;
    setSavingChannelId(id);
    await fetch('/api/channels', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id, name: editingChannelName.trim() }),
    });
    setEditingChannelId(null);
    setSavingChannelId(null);
    await fetchChannels();
  }

  // --- Ingest ---
  async function ingestBatch(videoUrls: string[]): Promise<IngestResult[]> {
    const res = await fetch('/api/ingest', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ urls: videoUrls }),
    });
    if (!res.ok) {
      const errText = await res.text();
      return videoUrls.map(url => ({ url, error: `サーバーエラー (${res.status}): ${errText.slice(0, 100)}` }));
    }
    return (await res.json() as { results: IngestResult[] }).results;
  }

  async function handleIngest() {
    const lines = urls.split('\n').map(l => l.trim()).filter(Boolean);
    if (lines.length === 0 || ingesting) return;
    setIngesting(true);
    setResults([]);
    setProgress(null);

    try {
      const bulkLine = lines.find(l => isBulkUrl(l));

      if (bulkLine) {
        setProgress({ current: 0, total: 0 });
        const plRes = await fetch('/api/playlist', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ url: bulkLine }),
        });
        if (!plRes.ok) {
          setResults([{ url: bulkLine, error: `取得エラー: ${(await plRes.text()).slice(0, 100)}` }]);
          return;
        }
        const { videoIds } = await plRes.json() as { videoIds: string[] };
        const videoUrls = videoIds.map(id => `https://www.youtube.com/watch?v=${id}`);
        setProgress({ current: 0, total: videoUrls.length });
        const allResults: IngestResult[] = [];
        for (let i = 0; i < videoUrls.length; i += BATCH_SIZE) {
          const batchResults = await ingestBatch(videoUrls.slice(i, i + BATCH_SIZE));
          allResults.push(...batchResults);
          setResults([...allResults]);
          setProgress({ current: Math.min(i + BATCH_SIZE, videoUrls.length), total: videoUrls.length });
        }
        fetchChannels();
        fetchVideos(1);
      } else {
        setProgress({ current: 0, total: lines.length });
        const allResults: IngestResult[] = [];
        for (let i = 0; i < lines.length; i += BATCH_SIZE) {
          const batchResults = await ingestBatch(lines.slice(i, i + BATCH_SIZE));
          allResults.push(...batchResults);
          setResults([...allResults]);
          setProgress({ current: Math.min(i + BATCH_SIZE, lines.length), total: lines.length });
        }
        if (allResults.some(r => !r.error)) { setUrls(''); fetchChannels(); fetchVideos(1); }
      }
    } catch (e) {
      setResults([{ url: '', error: `エラー: ${e instanceof Error ? e.message : String(e)}` }]);
    } finally {
      setIngesting(false);
      setProgress(null);
    }
  }

  // --- Select ---
  const allSelected = videos.length > 0 && videos.every(v => selected.has(v.id));
  const someSelected = selected.size > 0;

  function toggleSelectAll() {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(videos.map(v => v.id)));
  }

  function toggleSelect(id: string) {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  // --- Bulk assign channel ---
  async function handleAssignChannel(channelId: string) {
    if (!someSelected) return;
    setAssigningChannel(true);
    try {
      await fetch('/api/videos', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ channelId, videoIds: Array.from(selected) }),
      });
      fetchVideos(page);
    } finally {
      setAssigningChannel(false);
    }
  }

  // --- Delete ---
  async function handleDelete(id: string, title: string) {
    if (!confirm(`「${title}」を削除しますか？`)) return;
    setDeletingId(id);
    try {
      await fetch('/api/videos', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id }),
      });
      fetchVideos(page);
    } finally {
      setDeletingId(null);
    }
  }

  async function handleDeleteSelected() {
    if (!someSelected || !confirm(`選択した${selected.size}件を削除しますか？`)) return;
    for (const id of selected) {
      await fetch('/api/videos', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id }),
      });
    }
    fetchVideos(page);
  }

  const urlCount = urls.split('\n').filter(l => l.trim()).length;
  const hasBulk = urls.split('\n').some(l => isBulkUrl(l.trim()));

  return (
    <div className="max-w-4xl mx-auto px-6 py-12">
      <div className="fade-in mb-10">
        <h1 style={{ color: 'var(--color-text)', letterSpacing: '0.2em' }} className="text-2xl font-light mb-1">管理画面</h1>
        <p style={{ color: 'var(--color-muted)' }} className="text-sm">YouTube動画のインデックス管理</p>
      </div>

      {/* ── Channel Manager ── */}
      <div style={{ border: '1px solid var(--color-border)', backgroundColor: 'var(--color-card)' }} className="fade-in rounded-xl mb-5 overflow-hidden">
        <button
          onClick={() => setShowChannelManager(v => !v)}
          className="w-full flex items-center justify-between px-6 py-4 hover:bg-[var(--color-bg)] transition-colors"
        >
          <div className="flex items-center gap-2">
            <Settings2 size={14} style={{ color: 'var(--color-accent)' }} />
            <span style={{ color: 'var(--color-text)', letterSpacing: '0.1em' }} className="text-sm font-light">チャンネル管理</span>
            {channels.length > 0 && (
              <span style={{ color: 'var(--color-muted)' }} className="text-xs">（{channels.length}件）</span>
            )}
          </div>
          <span style={{ color: 'var(--color-muted)' }} className="text-xs">{showChannelManager ? '▲' : '▼'}</span>
        </button>

        {showChannelManager && (
          <div style={{ borderTop: '1px solid var(--color-border)' }} className="px-6 py-4">
            {channels.length === 0 ? (
              <p style={{ color: 'var(--color-muted)' }} className="text-xs">動画を取り込むとチャンネルが自動で追加されます</p>
            ) : (
              <div className="space-y-2">
                <p style={{ color: 'var(--color-muted)' }} className="text-xs mb-3">動画を取り込むと自動でチャンネルが追加されます。名称を変更できます。</p>
                {channels.map(ch => (
                  <div key={ch.id} className="flex items-center gap-3">
                    {editingChannelId === ch.id ? (
                      <>
                        <input
                          value={editingChannelName}
                          onChange={e => setEditingChannelName(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') saveChannelName(ch.id); if (e.key === 'Escape') setEditingChannelId(null); }}
                          autoFocus
                          style={{ border: '1px solid var(--color-accent)', color: 'var(--color-text)', backgroundColor: 'var(--color-bg)' }}
                          className="flex-1 text-sm px-3 py-1.5 rounded-lg outline-none"
                        />
                        <button
                          onClick={() => saveChannelName(ch.id)}
                          disabled={savingChannelId === ch.id}
                          style={{ color: 'var(--color-accent)' }}
                          className="p-1.5 hover:opacity-70 transition-opacity"
                        >
                          {savingChannelId === ch.id ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
                        </button>
                        <button onClick={() => setEditingChannelId(null)} style={{ color: 'var(--color-muted)' }} className="p-1.5 hover:opacity-70 transition-opacity">
                          <X size={13} />
                        </button>
                      </>
                    ) : (
                      <>
                        <span style={{ color: 'var(--color-text)' }} className="flex-1 text-sm">{ch.name}</span>
                        <button onClick={() => startEditChannel(ch)} style={{ color: 'var(--color-muted)' }} className="p-1.5 hover:text-[var(--color-accent)] transition-colors">
                          <Pencil size={12} />
                        </button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Ingest form ── */}
      <div style={{ border: '1px solid var(--color-border)', backgroundColor: 'var(--color-card)' }} className="fade-in fade-in-delay-1 rounded-xl p-6 mb-8">
        <h2 style={{ color: 'var(--color-text)', letterSpacing: '0.12em' }} className="text-sm font-light mb-1">動画を取り込む</h2>
        <p style={{ color: 'var(--color-muted)' }} className="text-xs mb-4">動画URL・プレイリストURL・チャンネルURL（@handle）に対応。チャンネルは自動で登録されます。</p>

        <textarea
          value={urls}
          onChange={e => setUrls(e.target.value)}
          placeholder={`https://www.youtube.com/watch?v=XXXXXXXXX\nhttps://www.youtube.com/playlist?list=XXXXXXXXX\nhttps://www.youtube.com/@channelHandle`}
          rows={4}
          style={{ border: '1px solid var(--color-border)', color: 'var(--color-text)', backgroundColor: 'var(--color-bg)', resize: 'vertical' }}
          className="w-full text-sm px-4 py-3 rounded-lg outline-none placeholder:text-[var(--color-muted)] focus:border-[var(--color-accent)] transition-colors font-mono leading-relaxed"
        />

        <div className="flex items-center justify-between mt-3">
          <p style={{ color: 'var(--color-muted)' }} className="text-xs flex items-center gap-1.5">
            {hasBulk && <ListVideo size={12} style={{ color: 'var(--color-accent)' }} />}
            {hasBulk ? 'プレイリスト/チャンネル（全動画を自動取り込み）' : urlCount > 0 ? `${urlCount}件のURL` : ''}
          </p>
          <button
            onClick={handleIngest}
            disabled={ingesting || urlCount === 0}
            style={{
              backgroundColor: !ingesting && urlCount > 0 ? 'var(--color-accent)' : 'var(--color-border)',
              color: !ingesting && urlCount > 0 ? 'white' : 'var(--color-muted)',
            }}
            className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm transition-all duration-300"
          >
            {ingesting ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            {ingesting
              ? progress ? `${progress.current} / ${progress.total}件処理中...` : '読み込み中...'
              : hasBulk ? '一括取り込む' : `${urlCount > 1 ? `${urlCount}件を` : ''}取り込む`
            }
          </button>
        </div>

        {ingesting && progress && progress.total > 0 && (
          <div className="mt-4">
            <div style={{ backgroundColor: 'var(--color-border)' }} className="w-full h-1 rounded-full overflow-hidden">
              <div style={{ width: `${(progress.current / progress.total) * 100}%`, backgroundColor: 'var(--color-accent)', transition: 'width 0.5s ease' }} className="h-full rounded-full" />
            </div>
            <p style={{ color: 'var(--color-muted)' }} className="text-xs mt-1.5 text-right">{Math.round((progress.current / progress.total) * 100)}%</p>
          </div>
        )}

        {results && results.length > 0 && (
          <div className="mt-4 space-y-2 max-h-48 overflow-y-auto">
            {results.map((r, i) => (
              <div key={i} style={{ backgroundColor: r.error ? 'rgba(196,90,58,0.08)' : 'rgba(138,158,135,0.1)', border: `1px solid ${r.error ? '#c45a3a' : 'var(--color-accent)'}`, color: r.error ? '#c45a3a' : 'var(--color-accent-hover)' }} className="flex items-start gap-2 px-4 py-2.5 rounded-lg text-xs">
                {r.error ? <XCircle size={12} className="flex-shrink-0 mt-0.5" /> : <CheckCircle size={12} className="flex-shrink-0 mt-0.5" />}
                <div>
                  <p className="font-light">{r.title ?? r.url}</p>
                  {r.error ? <p className="mt-0.5 opacity-80">{r.error}</p> : <p className="mt-0.5 opacity-80">{r.chunkCount}チャンク · チャンネル自動登録済み</p>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Video list ── */}
      <div className="fade-in fade-in-delay-2">

        {/* Filter bar */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <input
            value={filterSearch}
            onChange={e => { setFilterSearch(e.target.value); setPage(1); }}
            placeholder="タイトルで絞り込み..."
            style={{ border: '1px solid var(--color-border)', color: 'var(--color-text)', backgroundColor: 'var(--color-bg)' }}
            className="text-sm px-3 py-1.5 rounded-lg outline-none placeholder:text-[var(--color-muted)] focus:border-[var(--color-accent)] transition-colors flex-1 min-w-40"
          />
          <div className="flex gap-1.5 flex-wrap">
            <button onClick={() => setFilterChannel('')} style={{ border: `1px solid ${!filterChannel ? 'var(--color-accent)' : 'var(--color-border)'}`, backgroundColor: !filterChannel ? 'var(--color-accent)' : 'transparent', color: !filterChannel ? 'white' : 'var(--color-muted)' }} className="px-3 py-1.5 rounded-lg text-xs transition-all duration-200">すべて</button>
            {channels.map(ch => (
              <button key={ch.id} onClick={() => setFilterChannel(filterChannel === ch.id ? '' : ch.id)} style={{ border: `1px solid ${filterChannel === ch.id ? 'var(--color-accent)' : 'var(--color-border)'}`, backgroundColor: filterChannel === ch.id ? 'var(--color-accent)' : 'transparent', color: filterChannel === ch.id ? 'white' : 'var(--color-muted)' }} className="px-3 py-1.5 rounded-lg text-xs transition-all duration-200">{ch.name}</button>
            ))}
          </div>
          <p style={{ color: 'var(--color-muted)' }} className="text-xs ml-auto">{total}件</p>
        </div>

        {/* Table header */}
        <div style={{ border: '1px solid var(--color-border)', backgroundColor: 'var(--color-card)' }} className="flex items-center gap-3 px-4 py-2.5 rounded-t-xl">
          <button onClick={toggleSelectAll} className="flex-shrink-0 w-4 h-4 flex items-center justify-center">
            <div style={{ border: `1.5px solid ${allSelected ? 'var(--color-accent)' : 'var(--color-muted)'}`, backgroundColor: allSelected ? 'var(--color-accent)' : 'transparent' }} className="w-4 h-4 rounded flex items-center justify-center transition-all">
              {allSelected && <CheckCircle size={10} color="white" />}
            </div>
          </button>
          <span style={{ color: 'var(--color-muted)' }} className="text-xs flex-1">動画タイトル</span>
          <span style={{ color: 'var(--color-muted)' }} className="text-xs w-28 hidden sm:block">チャンネル</span>
          <span style={{ color: 'var(--color-muted)' }} className="text-xs w-20 hidden md:block text-center">書き起こし</span>
          <span style={{ color: 'var(--color-muted)' }} className="text-xs w-20 hidden lg:block text-right">取り込み日</span>
          <span className="w-16" />
        </div>

        {/* Rows */}
        {loadingVideos ? (
          <div style={{ border: '1px solid var(--color-border)', borderTop: 'none' }} className="rounded-b-xl flex justify-center py-12">
            <Loader2 size={18} className="animate-spin" style={{ color: 'var(--color-muted)' }} />
          </div>
        ) : videos.length === 0 ? (
          <div style={{ border: '1px solid var(--color-border)', borderTop: 'none' }} className="rounded-b-xl text-center py-12">
            <p style={{ color: 'var(--color-muted)' }} className="text-sm">動画がありません</p>
          </div>
        ) : (
          <div style={{ border: '1px solid var(--color-border)', borderTop: 'none' }} className="rounded-b-xl overflow-hidden divide-y divide-[var(--color-border)]">
            {videos.map(v => {
              const isSelected = selected.has(v.id);
              const hasTranscript = !!v.transcript_text;
              return (
                <div key={v.id} style={{ backgroundColor: isSelected ? 'rgba(138,158,135,0.07)' : 'var(--color-bg)' }} className="flex items-center gap-3 px-4 py-3 hover:bg-[var(--color-card)] transition-colors">
                  <button onClick={() => toggleSelect(v.id)} className="flex-shrink-0">
                    <div style={{ border: `1.5px solid ${isSelected ? 'var(--color-accent)' : 'var(--color-border)'}`, backgroundColor: isSelected ? 'var(--color-accent)' : 'transparent' }} className="w-4 h-4 rounded flex items-center justify-center transition-all">
                      {isSelected && <CheckCircle size={10} color="white" />}
                    </div>
                  </button>

                  <div className="flex-1 min-w-0">
                    <p style={{ color: 'var(--color-text)' }} className="text-sm font-light truncate">{v.title}</p>
                  </div>

                  <p style={{ color: 'var(--color-accent)' }} className="text-xs w-28 hidden sm:block truncate">{channelName(v.channel_id)}</p>

                  {/* Transcript status */}
                  <div className="w-20 hidden md:flex justify-center">
                    {hasTranscript
                      ? <span style={{ color: 'var(--color-accent)' }} className="flex items-center gap-1 text-xs"><FileText size={12} />済み</span>
                      : <span style={{ color: 'var(--color-muted)' }} className="flex items-center gap-1 text-xs"><FileX size={12} />未</span>
                    }
                  </div>

                  <p style={{ color: 'var(--color-muted)' }} className="text-xs w-20 hidden lg:block text-right">{new Date(v.indexed_at).toLocaleDateString('ja-JP', { month: 'short', day: 'numeric' })}</p>

                  <div className="flex items-center gap-1 w-16 justify-end">
                    <Link href={`/admin/videos/${v.id}`} style={{ color: 'var(--color-muted)' }} className="p-1.5 rounded hover:text-[var(--color-accent)] transition-colors" title="テキスト編集">
                      <Pencil size={12} />
                    </Link>
                    <button onClick={() => handleDelete(v.id, v.title)} disabled={deletingId === v.id} style={{ color: 'var(--color-muted)' }} className="p-1.5 rounded hover:text-[#c45a3a] transition-colors" title="削除">
                      {deletingId === v.id ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1}
              style={{ border: '1px solid var(--color-border)', color: page <= 1 ? 'var(--color-muted)' : 'var(--color-text)' }}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs transition-colors hover:border-[var(--color-accent)] disabled:opacity-40"
            >
              <ChevronLeft size={13} /> 前のページ
            </button>
            <p style={{ color: 'var(--color-muted)' }} className="text-xs">
              {page} / {totalPages} ページ（{total}件）
            </p>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              style={{ border: '1px solid var(--color-border)', color: page >= totalPages ? 'var(--color-muted)' : 'var(--color-text)' }}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs transition-colors hover:border-[var(--color-accent)] disabled:opacity-40"
            >
              次のページ <ChevronRight size={13} />
            </button>
          </div>
        )}
      </div>

      {/* ── Floating action bar (multi-select) ── */}
      {someSelected && (
        <div
          style={{ backgroundColor: 'var(--color-text)', border: '1px solid rgba(255,255,255,0.1)' }}
          className="fixed bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-3 px-5 py-3 rounded-2xl shadow-2xl z-50"
        >
          <span style={{ color: 'rgba(255,255,255,0.6)' }} className="text-xs mr-1">
            {selected.size}件選択中
          </span>

          <span style={{ backgroundColor: 'rgba(255,255,255,0.15)' }} className="w-px h-5" />

          <Tag size={13} style={{ color: 'rgba(255,255,255,0.6)' }} />
          <span style={{ color: 'rgba(255,255,255,0.6)' }} className="text-xs">チャンネル：</span>

          {channels.map(ch => (
            <button
              key={ch.id}
              onClick={() => handleAssignChannel(ch.id)}
              disabled={assigningChannel}
              style={{ backgroundColor: 'rgba(138,158,135,0.3)', color: 'white', border: '1px solid rgba(138,158,135,0.5)' }}
              className="px-3 py-1 rounded-full text-xs hover:bg-[rgba(138,158,135,0.5)] transition-colors"
            >
              {assigningChannel ? <Loader2 size={10} className="animate-spin" /> : ch.name}
            </button>
          ))}

          <span style={{ backgroundColor: 'rgba(255,255,255,0.15)' }} className="w-px h-5" />

          <button
            onClick={handleDeleteSelected}
            style={{ color: '#f87171' }}
            className="flex items-center gap-1 text-xs hover:opacity-80 transition-opacity"
          >
            <Trash2 size={12} /> 削除
          </button>

          <button
            onClick={() => setSelected(new Set())}
            style={{ color: 'rgba(255,255,255,0.5)' }}
            className="text-xs hover:opacity-80 ml-1"
          >
            ✕
          </button>
        </div>
      )}
    </div>
  );
}
